import React, { useState, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { parseChromeBookmarks, validateChromeBookmarkFile, getImportStats } from '../utils/chromeBookmarkParser';
import { PowerButton } from './power-theme';
import '../styles/ImportDialog.css';
import '../styles/DynamicPanel.css';

const ImportDialog = ({ isOpen, onClose, onImport, loading = false }) => {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parseResults, setParseResults] = useState(null);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);

  // Handle file selection
  const handleFileSelect = (file) => {
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.html')) {
      setError('Bitte wählen Sie eine HTML-Datei aus (.html)');
      return;
    }
    
    setSelectedFile(file);
    setError('');
    
    // Parse file immediately for preview
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const htmlContent = e.target.result;
        
        // Validate Chrome bookmark file
        if (!validateChromeBookmarkFile(htmlContent)) {
          setError('Dies scheint keine gültige Chrome-Lesezeichen-Datei zu sein.');
          setParseResults(null);
          return;
        }
        
        // Parse bookmarks
        const bookmarks = parseChromeBookmarks(htmlContent);
        const stats = getImportStats(bookmarks);
        
        setParseResults({ bookmarks, stats });
        setError('');
        
      } catch (err) {
        setError(`Fehler beim Lesen der Datei: ${err.message}`);
        setParseResults(null);
      }
    };
    
    reader.readAsText(file, 'UTF-8');
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Handle import
  const handleImport = async () => {
    if (!parseResults || !parseResults.bookmarks.length) {
      setError('Keine Lesezeichen zum Importieren gefunden');
      return;
    }

    setImporting(true);
    setError('');

    try {
      await onImport(parseResults.bookmarks, parseResults.stats);
      
      // Reset form
      setSelectedFile(null);
      setParseResults(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      onClose();
      
    } catch (err) {
      setError(`Import fehlgeschlagen: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (importing) return; // Prevent closing during import
    
    setSelectedFile(null);
    setParseResults(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  // Handle click on upload area
  const handleUploadAreaClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dynamic-panel">
      {/* Power Panel Container */}
      <div className="theme-power import-power-panel">

        {/* Power Headline */}
        <div className="import-power-headline">
          <div className="import-headline-left">
            <span className="import-headline-icon">📥</span>
            <h2 className="import-headline-title">
              Lesezeichen importieren
            </h2>
          </div>
        </div>

        {/* CTA Button - ganz oben, immer sichtbar (analog Export-Anzeige) */}
        <PowerButton
          variant="primary"
          type="button"
          icon={importing || loading ? null : '📥'}
          fullWidth
          onClick={handleImport}
          disabled={!parseResults || importing || loading}
        >
          {importing || loading ? (
            <>
              <span className="loading-spinner-inline"></span>
              Importiere...
            </>
          ) : (
            `${parseResults ? parseResults.stats.totalBookmarks : 0} Lesezeichen importieren`
          )}
        </PowerButton>

        {/* Power Dropzone */}
        <div
          className={`power-dropzone ${dragActive ? 'drag-active' : ''} ${selectedFile ? 'has-file' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleUploadAreaClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".html"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            disabled={importing}
          />

          {!selectedFile ? (
            <div className="dropzone-idle">
              <div className="dropzone-icon">📁</div>
              <p className="dropzone-text">Klicken Sie hier oder ziehen Sie Ihre Chrome-Lesezeichen-Datei hierher</p>
              <small className="dropzone-hint">Nur HTML-Dateien (.html) werden unterstützt</small>
            </div>
          ) : (
            <div className="dropzone-file-selected">
              <div className="file-status-icon">✓</div>
              <div className="file-details">
                <p className="file-name">{selectedFile.name}</p>
                <small className="file-size">{(selectedFile.size / 1024).toFixed(1)} KB</small>
              </div>
              {!importing && (
                <button
                  className="remove-file-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    setParseResults(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  title="Datei entfernen"
                >
                  ×
                </button>
              )}
            </div>
          )}
        </div>

        {/* Instructions Panel */}
        <div className="import-info-panel">
          <div className="info-panel-header">
            <h3 className="info-panel-title">
              📋 Anleitung
            </h3>
          </div>
          <ol className="import-steps">
            <li>Öffnen Sie Chrome und gehen Sie zu <code className="code-pill">chrome://bookmarks/</code></li>
            <li>Klicken Sie auf das Menü (⋮) oben rechts</li>
            <li>Wählen Sie "Lesezeichen exportieren"</li>
            <li>Speichern Sie die HTML-Datei auf Ihrem Computer</li>
            <li>Laden Sie die Datei hier hoch</li>
          </ol>
        </div>

        {/* Error Panel */}
        {error && (
          <div className="import-error-panel">
            <span className="error-icon">⚠️</span>
            <span className="error-text">{error}</span>
          </div>
        )}

        {/* Import Status Panel */}
        {parseResults && (
          <div className="import-status-panel">
            <div className="status-panel-header">
              <h3 className="status-panel-title">
                ✓ Lesezeichen erkannt
              </h3>
            </div>
            <div className="status-stats-grid">
              <div className="status-stat-item">
                <span className="status-stat-label">Lesezeichen:</span>
                <span className="status-stat-value">{parseResults.stats.totalBookmarks}</span>
              </div>
              <div className="status-stat-item">
                <span className="status-stat-label">Ordner/Tags:</span>
                <span className="status-stat-value">{parseResults.stats.uniqueFolders}</span>
              </div>
              <div className="status-stat-item">
                <span className="status-stat-label">Domains:</span>
                <span className="status-stat-value">{parseResults.stats.uniqueDomains}</span>
              </div>
            </div>

            {/* Preview Section */}
            <div className="import-preview-section">
              <h4 className="preview-section-title">Erste 3 Lesezeichen:</h4>
              {parseResults.bookmarks.slice(0, 3).map((bookmark, index) => (
                <div key={index} className="preview-bookmark-item">
                  <div className="preview-bookmark-title">{bookmark.title}</div>
                  <div className="preview-bookmark-url">{bookmark.url}</div>
                  {bookmark.description && (
                    <div className="preview-bookmark-desc">{bookmark.description}</div>
                  )}
                </div>
              ))}
              {parseResults.bookmarks.length > 3 && (
                <p className="preview-more">
                  ... und {parseResults.bookmarks.length - 3} weitere
                </p>
              )}
            </div>
          </div>
        )}

      </div>
      {/* End Power Panel */}
    </div>
  );
};

export default ImportDialog;